import { EXPLORER_PALETTE } from './fractalPalette';

// Vertical span is measured in complex-plane units. Derive horizontal span
// from the canvas proportions so both axes have the same units per pixel.
export function viewportFromCenter([x, y], verticalSpan, width, height) {
  const horizontalSpan = verticalSpan * width / height;
  return {
    xRange: [x - horizontalSpan / 2, x + horizontalSpan / 2],
    yRange: [y - verticalSpan / 2, y + verticalSpan / 2]
  };
}

function closeup(center, verticalSpan, height, gScale = 7, phaseOffset = 0, extraPalette = null) {
  const width = 1600;
  return {
    width,
    height,
    ...viewportFromCenter(center, verticalSpan, width, height),
    palette: { ...EXPLORER_PALETTE, gScale, phaseOffset, ...extraPalette }
  };
}

export const coloringCloseups = {
  // Upper continent shoulder showing the 1, 2, 3, 5, 8, 13, 21 Fibonacci bulb wakes with equipotential contours and external-ray spokes.
  equipotentialExternalRays: closeup([-0.44, -0.58], 0.75, 1200, 6.5, 0.15, { mode: 'equipotential_rings' }),
  // Binary Decomposition Parquet Mosaic in Seahorse Valley: 2.4e-3 vertical span with Douady-Hubbard binary angle decomposition.
  binaryDecompositionParquetMosaic: closeup([-0.745428, 0.113009], 0.0024, 1200, 5.89, 0.507, { mode: 'binary_cells' }),
  // Wolfgang Beyer zoom step 3: upside-down 25-spoke seahorse with complex-derivative 3D relief shading.
  derivativeNormalSeahorse: closeup([-0.74303, 0.126433], 0.0120825, 1200, 6.2, 0.48, { mode: 'normal_3d' }),
  // Wolfgang Beyer zoom step 5: simply connected zigzag path through 25-spoke hubs with Pickover stalk traps.
  pickoverStalkOrbitTraps: closeup([-0.7436499, 0.13188204], 0.0005535075, 1200, 7.5, 0.42, { mode: 'pickover_stalks' }),
  // Orbital Ring-Trap Cellular Web in Quad Spiral Valley: 3.6e-3 vertical span with |z| = 1.25 Gaussian ring orbit trap.
  orbitalRingTrapCellularWeb: closeup([0.28245, 0.4831], 0.0036, 1200, 7.27, 0.588, { mode: 'cellular_web' }),
  // Stripe-Average Lightning Vortex at c = i: 3.5e-3 vertical span with trigonometric stripe-average coloring.
  stripeLightningVortex: closeup([0, 1], 0.0035, 1200, 92.16, 0.71, { mode: 'stripe' }),
};

export const closeups = {
  // Upper continent shoulder showing the 1, 2, 3, 5, 8, 13, 21 Fibonacci bulb wakes in pure smooth escape-time coloring.
  fibonacciBulbWakes: closeup([-0.44, -0.58], 0.75, 1200, 6.5, 0.15),
  // Wolfgang Beyer zoom step 3: upside-down 25-spoke seahorse in pure smooth escape-time coloring.
  upsideDownSeahorse: closeup([-0.74303, 0.126433], 0.0120825, 1200, 6.2, 0.48),
  // Fill the frame with a seahorse spiral instead of the mostly solid valley
  // overview. A slower color cycle separates its arms; the phase shift places
  // the open water in dark blue and the finer curls in teal and warm ivory.
  seahorse: closeup([-0.747, 0.108], 0.006, 1200, 5, 0.5),
  // Seahorse Valley Spiral Sanctuary: 5.8e-3 vertical span with gScale 4 and phase 0.726.
  seahorseSpiralSanctuary: closeup([-0.747, 0.108], 0.0058, 1200, 4, 0.726),
  // Wolfgang Beyer zoom step 4: terminal Misiurewicz point at the center of a seahorse tail spiral.
  seahorseTailMisiurewicz: closeup([-0.7435669, 0.1314023], 0.00171585, 1200, 8.5, 0.62),
  // Wolfgang Beyer zoom step 5: simply connected zigzag path through 25-spoke hubs in pure smooth escape-time coloring.
  simplyConnectedTailPath: closeup([-0.7436499, 0.13188204], 0.0005535075, 1200, 7.5, 0.42),
  // Wolfgang Beyer zoom step 6: opposing dendritic seahorse tails forming a double-hook satellite gateway.
  doubleHookGateway: closeup([-0.74364085, 0.13182733], 0.00009051, 1200, 9.2, 0.52),
  elephant: closeup([0.3, 0], 0.1, 1200),
  antenna: closeup([-1.97845, 0], 0.04, 800, 9),
  // Period-doubling limit: https://www.mrob.com/pub/muency/feigenbaumpoint.html
  feigenbaum: closeup([-1.4011551890920506, 0], 0.065, 1200, 5, 0.5),
  // Double-spiral Julia medallion, figure 2: https://www.mrob.com/pub/muency/embeddedjuliaset.html
  juliaMedallion: closeup([-1.768620774, 0.002428273], 0.000009, 1200, 5, 0.5),
  // Elephant's Trunk: https://mandelbrot.neocities.org/gallery
  elephantTrunk: closeup([0.2777323864244548, 0.0073446267400780795], 0.000022258585623343838, 1200, 5, 0.5),
  // A double spiral on the period-2 side of Seahorse Valley.
  doubleSpiral: closeup([-0.7756838, 0.1364674], 0.0018, 1200, 5, 0.5),
  // Closer 4:3 framing of the Double Spiral Valley core with high-contrast lagoon cycling.
  doubleSpiralNexus: closeup([-0.77568377, 0.13646737], 0.0012, 1200, 14.21, 0.073),
  // Triple Spiral Valley: https://mandelbrot.neocities.org/gallery
  tripleSpiral: closeup([-0.087593732, 0.65509028], 0.0044, 1200),
  // Four-arm detail in https://www.mrob.com/pub/muency/r2c14.html
  quadSpiral: closeup([0.28245, 0.4831], 0.004, 1200, 5, 0.5),
  // Period-3 island: https://www.mrob.com/pub/muency/r2tseries.html
  miniMandelbrot: closeup([-1.759672, 0], 0.065, 1200, 5, 0.5),
  // Scepter detail (Seahorse's Horn): https://mandelbrot.neocities.org/gallery
  scepter: closeup([-1.25066, 0.02012], 0.0009, 1200),
  // Closer framing of the Scepter Valley crown at 5.2e-4 vertical span.
  scepterCrown: closeup([-1.25066, 0.02012], 0.00052, 1200, 10.36, 0.55),
  // Snowflake detail: https://mandelbrot.dev/
  snowflake: closeup([-0.658448, -0.466852], 0.00065, 1200, 5, 0.5),
  ...coloringCloseups,
};

function deepCloseup({ centerX, centerY, spanY, precisionDigits, iterationBudget, height = 1200, gScale = 5, phaseOffset = 0.5 }) {
  const width = 1600;
  return {
    width,
    height,
    camera: {
      version: 1,
      centerX,
      centerY,
      spanY,
      aspect: width / height,
      precisionDigits,
    },
    ...(iterationBudget ? { iterationBudget } : {}),
    palette: { ...EXPLORER_PALETTE, gScale, phaseOffset },
  };
}

export const substructureSections = [
  {
    section: 'fibonacci-bulb-wakes',
    headingId: 'fibonacci-bulb-wakes-heading',
    title: 'Fibonacci Bulb Wakes',
    description: 'Along the upper shoulder between the period-1 cardioid and the period-2 bulb, primary bulbs and their branching antenna wakes follow the Fibonacci sequence 1, 2, 3, 5, 8, 13, and 21, with the largest wake between periods 1 and 2 belonging to the period-3 bulb.',
    preset: closeups.fibonacciBulbWakes,
  },
  {
    section: 'upside-down-seahorse',
    headingId: 'upside-down-seahorse-heading',
    title: 'Upside-Down 25-Spoke Seahorse',
    description: 'Centred at \u22120.743030 + 0.126433i at a 1.21 \u00d7 10\u207b\u00b2 vertical span (191\u00d7 magnification), smooth escape-time contours outline the 25 spokes of a single inverted seahorse\u2014two groups of 12 spokes plus one link to the main cardioid\u2014meeting at a central Misiurewicz hub.',
    preset: closeups.upsideDownSeahorse,
  },
  {
    section: 'seahorse-valley',
    headingId: 'seahorse-valley-heading',
    title: 'Seahorse Valley',
    description: 'A close-up of the curled branches between the main cardioid and the period-2 bulb, centred near \u22120.747 + 0.108i.',
    preset: closeups.seahorse,
  },
  {
    section: 'seahorse-valley-spiral-sanctuary',
    headingId: 'seahorse-valley-spiral-sanctuary-heading',
    title: 'Seahorse Valley Spiral Sanctuary',
    description: 'Counter-rotating seahorse tails between the main cardioid and the period-2 bulb, centred near \u22120.747 + 0.108i at a 5.8 \u00d7 10\u207b\u00b3 vertical span, separating deep navy water from warm ivory and amber curls.',
    preset: closeups.seahorseSpiralSanctuary,
  },
  {
    section: 'seahorse-tail-misiurewicz',
    headingId: 'seahorse-tail-misiurewicz-heading',
    title: 'Seahorse Tail Misiurewicz Endpoint',
    description: 'Centred at \u22120.7435669 + 0.1314023i at a 1.72 \u00d7 10\u207b\u00b3 vertical span (1,345\u00d7 magnification), a seahorse tail winds into its terminal Misiurewicz point, where preperiodic orbits form an asymptotic logarithmic spiral.',
    preset: closeups.seahorseTailMisiurewicz,
  },
  {
    section: 'simply-connected-tail-path',
    headingId: 'simply-connected-tail-path-heading',
    title: 'Simply Connected Zigzag Tail Path',
    description: 'Centred at \u22120.74364990 + 0.13188204i at a 5.54 \u00d7 10\u207b\u2074 vertical span (4,169\u00d7 magnification), a single continuous filament zigzags through successive 25-spoke hubs toward the central spiral without forming closed loops.',
    preset: closeups.simplyConnectedTailPath,
  },
  {
    section: 'double-hook-gateway',
    headingId: 'double-hook-gateway-heading',
    title: 'Double-Hook Dendritic Gateway',
    description: 'Centred at \u22120.74364085 + 0.13182733i at a 9.05 \u00d7 10\u207b\u2075 vertical span (25,497\u00d7 magnification), two opposing dendritic seahorse tails curve inward to form the outer double-hook ring around a central satellite.',
    preset: closeups.doubleHookGateway,
  },
  {
    section: 'elephant-valley',
    headingId: 'elephant-valley-heading',
    title: 'Elephant Valley',
    description: 'The region near 0.3 on the real axis, with trunk-like spiral structures.',
    preset: closeups.elephant,
  },
  {
    section: 'antenna',
    headingId: 'antenna-heading',
    title: 'Antenna',
    description: 'The tip of the antenna at \u22122.0 \u2014 Misiurewicz point.',
    preset: closeups.antenna,
  },
  {
    section: 'feigenbaum-cascade',
    headingId: 'feigenbaum-cascade-heading',
    title: 'Feigenbaum Cascade',
    description: 'Follow the shrinking chain of bulbs as their periods double from 2 to 4, 8, 16, and beyond. Zoom toward the point near \u22121.401155 on the real axis to explore where the chain accumulates.',
    preset: closeups.feigenbaum,
  },
  {
    section: 'julia-medallion',
    headingId: 'julia-medallion-heading',
    title: 'Double-spiral Julia Medallion',
    description: "Near a miniature Mandelbrot's Seahorse Valley, curling filaments form a pattern resembling a Julia set. Explore the paired spirals and the smaller curls along their edges.",
    preset: closeups.juliaMedallion,
  },
  {
    section: 'elephant-trunk',
    headingId: 'elephant-trunk-heading',
    title: 'Elephant\u2019s Trunk',
    description: 'Deeper inside Elephant Valley, long curved branches curl into tightly wound tips. Follow a trunk to find smaller versions of the same pattern along its sides.',
    preset: closeups.elephantTrunk,
  },
  {
    section: 'double-spiral-valley',
    headingId: 'double-spiral-heading',
    title: 'Double Spiral Valley',
    description: 'On the period-2 bulb side of Seahorse Valley, two intertwined arms wind into a shared centre. Zoom in to find smaller curls repeating along their branching edges.',
    preset: closeups.doubleSpiral,
  },
  {
    section: 'double-spiral-nexus',
    headingId: 'double-spiral-nexus-heading',
    title: 'Double Spiral Nexus',
    description: 'Centred near \u22120.77568377 + 0.13646737i at a 1.2 \u00d7 10\u207b\u00b3 vertical span, paired logarithmic spirals wind into a shared preperiodic core on the period-2 bulb side of Seahorse Valley.',
    preset: closeups.doubleSpiralNexus,
  },
  {
    section: 'triple-spiral-valley',
    headingId: 'triple-spiral-heading',
    title: 'Triple Spiral Valley',
    description: 'Near the upper period-3 bulb, the boundary curls into spirals with three arms. Zoom in to follow the smaller spirals along each branch.',
    preset: closeups.tripleSpiral,
  },
  {
    section: 'quad-spiral-valley',
    headingId: 'quad-spiral-heading',
    title: 'Quad Spiral Valley',
    description: 'Near the upper period-4 bulb, the filaments form spirals with four arms. Follow each arm outward to see it branch into smaller spirals, adding another turn to the patterns of Double and Triple Spiral Valley.',
    preset: closeups.quadSpiral,
  },
  {
    section: 'mini-mandelbrot',
    headingId: 'mini-mandelbrot-heading',
    title: 'Mini Mandelbrot',
    description: 'Near \u22121.75 on the real axis, a miniature Mandelbrot set appears among delicate filaments. This period-3 island repeats the familiar cardioid and bulbs, with its own valleys waiting to be explored.',
    preset: closeups.miniMandelbrot,
  },
  {
    section: 'scepter-valley',
    headingId: 'scepter-heading',
    title: 'Scepter Valley',
    description: 'Also known as Seahorse Valley West, this region lies between the period-2 and period-4 bulbs. Its curled filaments sprout long, branching spikes that resemble ornate scepters.',
    preset: closeups.scepter,
  },
  {
    section: 'scepter-crown',
    headingId: 'scepter-crown-heading',
    title: 'Scepter Crown',
    description: 'Centred near \u22121.25066 + 0.02012i at a 5.2 \u00d7 10\u207b\u2074 vertical span inside Scepter Valley, spiked arches and counter-rotating double spirals link into an ornate diagonal crown.',
    preset: closeups.scepterCrown,
  },
  {
    section: 'snowflake',
    headingId: 'snowflake-heading',
    title: 'Snowflake',
    description: 'Fine, feathery branches form clusters resembling snowflakes near \u22120.658448 \u2212 0.466852i. Zoom in to find the pattern repeating along smaller branches.',
    preset: closeups.snowflake,
  },
];

export const extraDeepSubstructureSections = [
  {
    section: 'concentric-satellite-crowns',
    headingId: 'concentric-satellite-crowns-heading',
    title: 'Concentric Satellite Crowns',
    description: 'Centred at \u22120.743643135 + 0.131825963i at a 1.10 \u00d7 10\u207b\u2075 vertical span (210,350\u00d7 magnification), concentric crowns of seahorse tails double in count with each inward ring around a distorted period-28 satellite minibrot.',
    preset: deepCloseup({
      centerX: '-0.743643135',
      centerY: '0.131825963',
      spanY: '0.000010971',
      precisionDigits: 64,
      iterationBudget: 2500,
      gScale: 10.5,
      phaseOffset: 0.55,
    }),
  },
  {
    section: 'second-order-satellite-double-spirals',
    headingId: 'second-order-satellite-double-spirals-heading',
    title: 'Second-Order Satellite Double Spirals',
    description: 'At 3.70 \u00d7 10\u207b\u2078 vertical span (6.24 \u00d7 10\u2077\u00d7 magnification) inside the satellite\u2019s own Seahorse Valley, double spirals sprout seahorse-tail appendices along their outer rims and harbour second-order satellites.',
    preset: deepCloseup({
      centerX: '-0.743643900055',
      centerY: '0.131825890901',
      spanY: '3.6978e-8',
      precisionDigits: 64,
      iterationBudget: 4500,
      gScale: 13.5,
      phaseOffset: 0.18,
    }),
  },
  {
    section: 'cantor-embedded-julia-island',
    headingId: 'cantor-embedded-julia-island-heading',
    title: 'Cantor-Like Embedded Julia Island',
    description: 'At 3.85 \u00d7 10\u207b\u00b9\u00b9 vertical span (6.00 \u00d7 10\u00b9\u2070\u00d7 magnification) in the outer appendices of the satellite, an embedded Julia set that resembles a disconnected Cantor dust at low iterations resolves into a single connected filigree island.',
    preset: deepCloseup({
      centerX: '-0.743643887037151',
      centerY: '0.131825904205330',
      spanY: '3.847425e-11',
      precisionDigits: 64,
      iterationBudget: 5000,
      gScale: 16,
      phaseOffset: 0.35,
    }),
  },
  {
    section: 'elephant-trunk-crown',
    headingId: 'elephant-trunk-crown-heading',
    title: 'Elephant\u2019s Trunk Filaments',
    description: 'At 10\u207b\u00b9\u2070 vertical span inside an Elephant Valley spiral, compensated double-single and perturbation passes resolve fine concentric rings around a buried miniature spiral.',
    preset: deepCloseup({
      centerX: '0.2777323864247658024846244845667871839206537336346972441140836563',
      centerY: '0.007344626739844827636531636531636569078315472800614000934953734452607675',
      spanY: '9.222493788438788563594384909335380003116512448175358920843214287e-11',
      precisionDigits: 64,
    }),
  },
  {
    section: 'seahorse-spiral-island-nucleus',
    headingId: 'seahorse-spiral-island-nucleus-heading',
    title: 'Seahorse Spiral Island Nucleus',
    description: 'At 6.50 \u00d7 10\u207b\u00b2\u00b9 vertical span around the period-8007 nucleus at \u22120.7436438870371587 + 0.1318259042053120i, four self-similar spiral arms and nested satellite medallions converge on the core of the embedded Julia island.',
    preset: deepCloseup({
      centerX: '-0.743643887037158704752191506114779778215256207948181289816883901818560735783796478634787822762144',
      centerY: '0.131825904205311970493132056385140678972952279328918966209127677465513481738298337234738964180367',
      spanY: '6.5e-21',
      precisionDigits: 96,
      iterationBudget: 10500,
      gScale: 28,
      phaseOffset: 0.12,
    }),
  },
  {
    section: 'seahorse-labyrinthine-spiral',
    headingId: 'seahorse-labyrinthine-spiral-heading',
    title: 'Seahorse Labyrinthine Spiral',
    description: 'At 10\u207b\u00b2\u00b2 span deep inside Seahorse Valley, standard 64-bit floats collapse into flat blocks while a single high-precision reference orbit and Bivariate Linear Approximation keep every filament sharp.',
    preset: deepCloseup({
      centerX: '-0.7749619689162054160603187579709720260170694275270884029815610349',
      centerY: '0.1371094358363331588622515252914963788812477032142625933695428539',
      spanY: '1e-22',
      precisionDigits: 64,
      gScale: 14,
      phaseOffset: 0.5,
    }),
  },
  {
    section: 'dendrite-branch-tip',
    headingId: 'dendrite-branch-tip-heading',
    title: 'Misiurewicz Dendrite Tip at i',
    description: 'Zoomed to 10\u207b\u00b3\u2070 around the preperiodic Misiurewicz point c = i, self-similar lightning branches twist at fixed angles around the terminal tip of the upper filament.',
    preset: deepCloseup({
      centerX: '1e-32',
      centerY: '1',
      spanY: '1e-30',
      precisionDigits: 128,
      gScale: 28,
      phaseOffset: 0.5,
    }),
  },
  {
    section: 'sixteen-ray-crystalline-pine-compass',
    headingId: 'sixteen-ray-crystalline-pine-compass-heading',
    title: 'Sixteen-Ray Crystalline Pine Compass',
    description: 'At 1.02 \u00d7 10\u207b\u00b3\u2074 vertical span on the real axis near \u22121.4303584327, second-order inflection around a period-70 nucleus doubles an eight-branch Koch candelabrum into a sixteen-ray crystalline compass rose.',
    preset: deepCloseup({
      centerX: '-1.430358432726827034066973424214930206691553358775876966359894985802970759204483018474855054742573340561926753064460565056347223899006847872505324774267760013128155503631619306287307536886086292245786516740764412412552637',
      centerY: '0',
      spanY: '1.02e-34',
      precisionDigits: 256,
      iterationBudget: 7000,
      gScale: 68.78,
      phaseOffset: 0.874,
    }),
  },
  {
    section: 'stained-glass-cathedral-rose-window',
    headingId: 'stained-glass-cathedral-rose-window-heading',
    title: 'Stained-Glass Cathedral Rose Window',
    description: 'At 2.03 \u00d7 10\u207b\u2074\u00b2 vertical span inside Seahorse Valley, twenty-four radial filigree spokes and an eight-fold ring of golden crescents frame a nested central oculus.',
    preset: deepCloseup({
      centerX: '-0.74511656596787420214995002910827329312386742868874338836750221809762203119224063707893486009768706820719403115198702877913368907',
      centerY: '0.11296673260810894689677225432456390922901758049569387913765880747001595131956979787996967164122048654645653086608096309021749208',
      spanY: '2.028e-42',
      precisionDigits: 128,
      iterationBudget: 8000,
      gScale: 13.63,
      phaseOffset: 0.305,
    }),
  },
  {
    section: 'quad-spiral-octagonal-star-sanctuary',
    headingId: 'quad-spiral-octagonal-star-sanctuary-heading',
    title: 'Quad-Spiral Octagonal Star Sanctuary',
    description: 'At 5.18 \u00d7 10\u207b\u2075\u2074 vertical span inside Quad Spiral Valley, second-order inflection around a buried Quad Spiral nucleus resolves a multi-ringed octagonal starburst sanctuary of eight radial spiral arms.',
    preset: deepCloseup({
      centerX: '0.2829900580526514042749304920058806181797879170209492299057441436439992582678827553994065141374035095114800311274760313379977057515827633951655379064180569761809372491008227717662804365763462607246810156344007685216832597',
      centerY: '0.4826159058121791200329618905770803808034432394319520896216954726273828645814901523913419584965081776301973695215471050731911319821934813153255050355501297344212316215261593662734322900442655135955354510385682908239926625',
      spanY: '5.184e-54',
      precisionDigits: 256,
      iterationBudget: 8500,
      gScale: 44.1,
      phaseOffset: 0.096,
    }),
  },
  {
    section: 'western-needle-cascade',
    headingId: 'western-needle-cascade-heading',
    title: 'Western Needle at 10\u207b\u2076\u2070',
    description: 'Past the 10\u207b\u00b3\u2078 hardware float exponent limit near the western antenna tip at \u22122, detached-exponent floatExp arithmetic and the WebAssembly libmpdec kernel trace filaments across 60 orders of magnitude.',
    preset: deepCloseup({
      centerX: '-1.94143082454420927686287945954368100716482731930099808479506980688385890915155719783227919879740957232034119892570125585078025990124839704913245797414384956714071190107572046772555824293745566',
      centerY: '0.000671287899669390303095184227388694273148979623254059806265153789767428141679298820886609736995812196344141306070878792750727714503197248677197422074850009923574691984512076258070143990191366271',
      spanY: '1e-60',
      precisionDigits: 192,
      gScale: 18,
      phaseOffset: 0.25,
    }),
  },
  {
    section: 'transfinite-antenna-boundary',
    headingId: 'transfinite-antenna-boundary-heading',
    title: 'Transfinite Antenna Boundary at 10\u207b\u00b9\u2070\u2070',
    description: 'A googol-scale (10\u00b9\u2070\u2070\u00d7) magnification along the off-axis boundary near c = \u22122, where 256-digit libmpdec reference orbits and full-range GPU perturbation resolve sub-float32 geometry.',
    preset: deepCloseup({
      centerX: '-1.985460395476908299866880996806879451981048331723158600861770097228839495407084106315315787122567667823650737301907546790261165429362639415840482184826305124848955512626152165276014836226368249759473450642769479998615902014665081818100561873538007993620731',
      centerY: '0.0006056014468328159563154268022967937044489988250980377042041359707663998274194558313840037277113559123185170520253262943978823202032957369893619181751593420569566732588310011097003132141943642735758445278542990728253219480528713823058846004171717141554383834',
      spanY: '1e-100',
      precisionDigits: 256,
      gScale: 48,
      phaseOffset: 0.15,
    }),
  },
  {
    section: 'octogintillion-scepter-star-heart',
    headingId: 'octogintillion-scepter-star-heart-heading',
    title: 'Octogintillion Scepter Star-Heart',
    description: 'At 2.86 \u00d7 10\u207b\u00b9\u00b9\u00b9 vertical span inside Scepter Valley, concentric scepter coronas and radiant filigree rings surround a buried satellite minibrot beyond 10\u207b\u00b9\u2070\u2070.',
    preset: deepCloseup({
      centerX: '-1.25064414340362951564968783283546654076664874037458673666664420010848104299335428123964400504439304958957788529149991599773851028379632154271013182121231106767397192596615086296696883183609176098952083045636709811101779645702588044995400263',
      centerY: '0.0201585939777545512142500911084455679169351219868756478660327428309792765348714946448573658611550993154541567512961369292084142661963931211989931332487459063911959823238264080098254176927686266871798892500957420368347596466696025593632704668',
      spanY: '2.862e-111',
      precisionDigits: 256,
      iterationBudget: 9500,
      gScale: 15.06,
      phaseOffset: 0.705,
    }),
  },
];

export const allSubstructureSections = [
  ...substructureSections,
  ...extraDeepSubstructureSections,
];

function presetSpanX(preset) {
  if (preset.camera) {
    return Number(preset.camera.spanY) * (preset.camera.aspect || 1);
  }
  return preset.xRange[1] - preset.xRange[0];
}

export const OVERVIEW_SPAN_FLOOR = 1e-3;
export const DEEP_ZOOM_SPAN_FLOOR = 1e-16;
export const LABYRINTHINE_ZOOM_SPAN_FLOOR = 1e-38;

export const overviewPresets = allSubstructureSections.filter(
  ({ preset }) => presetSpanX(preset) >= OVERVIEW_SPAN_FLOOR
);

export const deepZoomPresets = allSubstructureSections.filter(({ preset }) => {
  const spanX = presetSpanX(preset);
  return spanX < OVERVIEW_SPAN_FLOOR && spanX >= DEEP_ZOOM_SPAN_FLOOR;
});

export const labyrinthineZoomPresets = allSubstructureSections.filter(({ preset }) => {
  const spanX = presetSpanX(preset);
  return spanX < DEEP_ZOOM_SPAN_FLOOR && spanX >= LABYRINTHINE_ZOOM_SPAN_FLOOR;
});

export const transfiniteZoomPresets = allSubstructureSections.filter(
  ({ preset }) => presetSpanX(preset) < LABYRINTHINE_ZOOM_SPAN_FLOOR
);
